import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { EmployeeFormatter } from "../../../helper/formatting";
import CompanySidebarEmptyState from "./CompanySidebarEmptyState";
import { SIDEBAR } from "./sidebarVisual";
import { SidebarSectionTitle } from "./SidebarPrimitives";

const AXIS_TICK = "#9AA0A6";
const GRID_STROKE = "rgba(88, 89, 91, 0.10)";

function CompareTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point || point.value == null) return null;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "8px",
        px: 1.35,
        py: 0.9,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12), 0 1px 2px rgba(15, 23, 42, 0.06)",
        border: "1px solid rgba(88, 89, 91, 0.08)",
        minWidth: 104,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: "text.primary",
          letterSpacing: "-0.01em",
          mb: 0.35,
          lineHeight: 1.2,
        }}
      >
        {point.name}
      </Typography>
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 600,
          color: "primary.main",
          lineHeight: 1.25,
          display: "flex",
          gap: 0.75,
          alignItems: "baseline",
        }}
      >
        <Box component="span" sx={{ opacity: 0.85 }}>
          Headcount
        </Box>
        <Box component="span">{EmployeeFormatter.format(point.value)}</Box>
      </Typography>
    </Box>
  );
}

CompareTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
};

/**
 * Compact bar comparison for Total / FTE / Online headcount point estimates.
 * Honest to available AlphaSearch fields — not a historical series.
 */
export default function HeadcountCompareChart({
  totalHeadcount,
  fteHeadcount,
  onlineHeadcount,
  accentColor = "#4A2CF5",
  height = 132,
  compact = false,
}) {
  const data = [
    { name: "Total", value: totalHeadcount?.pointEstimate },
    { name: "FTE", value: fteHeadcount?.pointEstimate },
    { name: "Online", value: onlineHeadcount?.pointEstimate },
  ].filter((row) => typeof row.value === "number" && Number.isFinite(row.value));

  if (data.length === 0) {
    return (
      <Box data-testid="headcount-compare-chart-empty">
        <SidebarSectionTitle sx={{ mb: SIDEBAR.gap.withinCard }}>
          Headcount breakdown
        </SidebarSectionTitle>
        <CompanySidebarEmptyState
          variant="chart"
          compact
          title="No breakdown available"
          description="Total, FTE, and online headcount estimates aren’t available yet."
          testId="headcount-compare-empty-state"
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }} data-testid="headcount-compare-chart">
      <SidebarSectionTitle sx={{ mb: SIDEBAR.gap.withinCard }}>
        Headcount breakdown
      </SidebarSectionTitle>
      <Box sx={{ width: "100%", height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: compact ? 11 : 10, fill: AXIS_TICK, fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={(value) => EmployeeFormatter.format(value)}
              tick={{ fontSize: 10, fill: AXIS_TICK, fontWeight: 500 }}
              tickLine={false}
              axisLine={false}
              width={compact ? 36 : 42}
              hide={compact}
            />
            <Tooltip
              trigger={compact ? "click" : "hover"}
              content={<CompareTooltip />}
              cursor={{ fill: accentColor, fillOpacity: 0.05 }}
              isAnimationActive={false}
            />
            <Bar dataKey="value" radius={[5, 5, 0, 0]} maxBarSize={compact ? SIDEBAR.metrics.compactBarMaxSize : SIDEBAR.metrics.barMaxSize} isAnimationActive={false}>
              {data.map((entry) => (
                <Cell key={entry.name} fill={accentColor} fillOpacity={0.88} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

HeadcountCompareChart.propTypes = {
  totalHeadcount: PropTypes.shape({ pointEstimate: PropTypes.number }),
  fteHeadcount: PropTypes.shape({ pointEstimate: PropTypes.number }),
  onlineHeadcount: PropTypes.shape({ pointEstimate: PropTypes.number }),
  accentColor: PropTypes.string,
  height: PropTypes.number,
  compact: PropTypes.bool,
};
